// Géométrie de l'éditeur de zones, indépendante de tout moteur de carte.
// Convention GeoJSON : une position est [longitude, latitude]. Google Maps parle en
// { lat, lng } : la conversion passe toujours par ce module, jamais à la main.

export type LngLat = [number, number]

export interface LatLng {
  lat: number
  lng: number
}

/** Polygone GeoJSON du domaine : anneau extérieur puis trous, chacun fermé. */
export interface PolygonGeometry {
  type: 'Polygon'
  coordinates: LngLat[][]
}

/**
 * Contour d'une zone en édition : sommets sans point de fermeture. Les trous d'une géométrie
 * chargée sont conservés tels quels; l'éditeur ne modifie que le contour extérieur.
 */
export interface EditableShape {
  ring: LngLat[]
  holes: LngLat[][]
}

export function toLatLng([lng, lat]: LngLat): LatLng {
  return { lat, lng }
}

export function fromLatLng({ lat, lng }: LatLng): LngLat {
  return [lng, lat]
}

function samePosition(a: LngLat | undefined, b: LngLat | undefined): boolean {
  return b !== undefined && a?.[0] === b[0] && a[1] === b[1]
}

/** Retire le point de fermeture d'un anneau GeoJSON, s'il est présent. */
export function openRing(ring: readonly (readonly number[])[]): LngLat[] {
  const positions = ring.map((position): LngLat => [position[0] ?? Number.NaN, position[1] ?? Number.NaN])
  if (positions.length > 1 && samePosition(positions[0], positions.at(-1))) positions.pop()
  return positions
}

/** Ferme un anneau ouvert en répétant son premier sommet. */
export function closeRing(ring: readonly LngLat[]): LngLat[] {
  const first = ring[0]
  if (!first) return []
  return samePosition(first, ring.at(-1)) ? [...ring] : [...ring, [first[0], first[1]]]
}

export function shapeFromPolygon(geometry: {
  coordinates: readonly (readonly (readonly number[])[])[]
}): EditableShape {
  const [outer = [], ...holes] = geometry.coordinates
  return { ring: openRing(outer), holes: holes.map((hole) => openRing(hole)) }
}

export function polygonFromShape(shape: EditableShape): PolygonGeometry {
  return {
    type: 'Polygon',
    coordinates: [closeRing(shape.ring), ...shape.holes.map((hole) => closeRing(hole))],
  }
}

export type ShapeIssue = 'TOO_FEW_VERTICES' | 'INVALID_COORDINATES' | 'SELF_INTERSECTION' | 'EMPTY_AREA'

export const shapeIssueMessage: Record<ShapeIssue, string> = {
  TOO_FEW_VERTICES: 'Une surface demande au moins trois points.',
  INVALID_COORDINATES: 'Un point est hors de la carte. Déplacez-le pour le corriger.',
  SELF_INTERSECTION: 'Le tracé se croise. Déplacez un point pour le corriger.',
  EMPTY_AREA: 'La surface est vide. Écartez les points pour la corriger.',
}

function validPosition([lng, lat]: LngLat): boolean {
  return Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 90
}

function orientation(a: LngLat, b: LngLat, c: LngLat): number {
  const value = (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1])
  return value === 0 ? 0 : value > 0 ? 1 : -1
}

function onSegment(a: LngLat, b: LngLat, c: LngLat): boolean {
  return (
    Math.min(a[0], c[0]) <= b[0] &&
    b[0] <= Math.max(a[0], c[0]) &&
    Math.min(a[1], c[1]) <= b[1] &&
    b[1] <= Math.max(a[1], c[1])
  )
}

function segmentsIntersect(p1: LngLat, q1: LngLat, p2: LngLat, q2: LngLat): boolean {
  const o1 = orientation(p1, q1, p2)
  const o2 = orientation(p1, q1, q2)
  const o3 = orientation(p2, q2, p1)
  const o4 = orientation(p2, q2, q1)
  if (o1 !== o2 && o3 !== o4) return true
  if (o1 === 0 && onSegment(p1, p2, q1)) return true
  if (o2 === 0 && onSegment(p1, q2, q1)) return true
  if (o3 === 0 && onSegment(p2, p1, q2)) return true
  return o4 === 0 && onSegment(p2, q1, q2)
}

/** Vrai si deux côtés non adjacents de l'anneau ouvert se touchent ou se croisent. */
export function ringSelfIntersects(ring: readonly LngLat[]): boolean {
  const count = ring.length
  for (let i = 0; i < count; i += 1) {
    const a1 = ring[i]
    const a2 = ring[(i + 1) % count]
    if (!a1 || !a2) continue
    for (let j = i + 1; j < count; j += 1) {
      // Côtés voisins : ils partagent un sommet, ce n'est pas un croisement.
      if (j === i || (j + 1) % count === i || (i + 1) % count === j) continue
      const b1 = ring[j]
      const b2 = ring[(j + 1) % count]
      if (b1 && b2 && segmentsIntersect(a1, a2, b1, b2)) return true
    }
  }
  return false
}

/** Aire signée en degrés², seulement pour détecter un anneau dégénéré — jamais une superficie. */
function planarSignedArea(ring: readonly LngLat[]): number {
  let total = 0
  for (let index = 0; index < ring.length; index += 1) {
    const current = ring[index]
    const next = ring[(index + 1) % ring.length]
    if (current && next) total += current[0] * next[1] - next[0] * current[1]
  }
  return total / 2
}

/** Premier défaut bloquant d'un contour, ou `null` s'il peut être enregistré. */
export function validateRing(ring: readonly LngLat[]): ShapeIssue | null {
  if (!ring.every(validPosition)) return 'INVALID_COORDINATES'
  const distinct = new Set(ring.map(([lng, lat]) => `${String(lng)},${String(lat)}`))
  if (ring.length < 3 || distinct.size < 3) return 'TOO_FEW_VERTICES'
  if (ringSelfIntersects(ring)) return 'SELF_INTERSECTION'
  if (planarSignedArea(ring) === 0) return 'EMPTY_AREA'
  return null
}

/** Cadre englobant de contours, pour « Voir toutes les zones ». `null` si rien à cadrer. */
export function boundsOf(rings: readonly (readonly LngLat[])[]): {
  north: number
  south: number
  east: number
  west: number
} | null {
  let north = -Infinity
  let south = Infinity
  let east = -Infinity
  let west = Infinity
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      if (!validPosition([lng, lat])) continue
      north = Math.max(north, lat)
      south = Math.min(south, lat)
      east = Math.max(east, lng)
      west = Math.min(west, lng)
    }
  }
  return Number.isFinite(north) ? { north, south, east, west } : null
}

const earthRadiusM = 6371008.8

/** Distance géodésique (haversine) entre deux positions, en mètres. */
export function distanceM(a: LngLat, b: LngLat): number {
  const toRadians = (value: number) => (value * Math.PI) / 180
  const dLat = toRadians(b[1] - a[1])
  const dLng = toRadians(b[0] - a[0])
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(a[1])) * Math.cos(toRadians(b[1])) * Math.sin(dLng / 2) ** 2
  return 2 * earthRadiusM * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Longueur de chaque côté d'un contour fermé (le dernier relie au premier sommet). */
export function sideLengthsM(ring: readonly LngLat[]): number[] {
  if (ring.length < 2) return []
  return ring.map((vertex, index) => distanceM(vertex, ring[(index + 1) % ring.length] ?? vertex))
}

/** Longueur d'une ligne ouverte (mesure de distance temporaire). */
export function pathLengthM(path: readonly LngLat[]): number {
  let total = 0
  for (let index = 1; index < path.length; index += 1) {
    const previous = path[index - 1]
    const current = path[index]
    if (previous && current) total += distanceM(previous, current)
  }
  return total
}

/**
 * Aire sphérique d'un anneau (ouvert ou fermé), en m². Aperçu seulement : la superficie qui
 * compte est recalculée par le serveur sous PostGIS.
 */
export function ringAreaM2(ring: readonly LngLat[]): number {
  const closed = closeRing(ring)
  if (closed.length < 4) return 0
  const radius = 6378137
  const toRadians = (value: number) => (value * Math.PI) / 180
  let total = 0
  for (let index = 0; index < closed.length - 1; index += 1) {
    const current = closed[index]
    const next = closed[index + 1]
    if (!current || !next) continue
    total +=
      (toRadians(next[0]) - toRadians(current[0])) *
      (2 + Math.sin(toRadians(current[1])) + Math.sin(toRadians(next[1])))
  }
  return Math.abs((total * radius * radius) / 2)
}

/** Aire d'une forme : contour extérieur moins ses trous. */
export function shapeAreaM2(shape: EditableShape): number {
  return Math.max(0, ringAreaM2(shape.ring) - shape.holes.reduce((sum, hole) => sum + ringAreaM2(hole), 0))
}
